import React from 'react';
import SiteLayout from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';


const AboutPage = () => {
  const { content: cmsContent, loading } = useCMSContent('about-page');

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Use CMS content only - no fallbacks
  const hero = cmsContent?.hero;
  const story = cmsContent?.story;
  const vision = cmsContent?.vision;
  const differentiators = cmsContent?.differentiators;
  const differentiatorsTitle = cmsContent?.differentiatorsTitle;
  const cta = cmsContent?.cta;

  // Show error if required CMS content is not configured
  if (!hero) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              About page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/about-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {hero && (
        <section className="py-20 bg-muted/30">
          <div className="container-wide text-center">
            {hero.title && (
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {hero.title}
              </h1>
            )}
            {hero.subtitle && (
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {hero.subtitle}
              </p>
            )}
            {hero.heroImage && (
              <div className="mt-12 relative mx-auto max-w-5xl rounded-xl overflow-hidden shadow-2xl border border-border/50">
                <img
                  src={hero.heroImage}
                  alt={hero.heroImageAlt || ''}
                  className="w-full h-auto object-cover"
                />
              </div>
            )}
          </div>
        </section>
      )}

      <section className="py-20">
        <div className="container-wide max-w-4xl">
          <div className="prose prose-lg mx-auto">
            {story && (
              <>
                {story.title && <h3>{story.title}</h3>}
                {(story.paragraphs || []).map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </>
            )}

            {vision && (
              <>
                {vision.title && <h3>{vision.title}</h3>}
                {(vision.paragraphs || []).map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </>
            )}

            {differentiators && differentiators.length > 0 && (
              <div className="bg-primary/5 p-8 rounded-xl my-12 not-prose border border-primary/10">
                <h4 className="text-xl font-bold mb-6 text-primary">{differentiatorsTitle}</h4>
                <ul className="space-y-6">
                  {differentiators.map((item, index) => (
                    <li key={index} className="flex gap-4">
                      <div className="font-bold text-primary text-lg">{item.number}.</div>
                      <div>
                        <strong className="text-foreground">{item.title}:</strong>{' '}
                        <span className="text-muted-foreground">{item.description}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {cta && (
        <section className="py-20 bg-primary text-white text-center">
          <div className="container-wide">
            {cta.title && <h2 className="text-3xl font-bold mb-6">{cta.title}</h2>}
            {cta.button && cta.href && (
              <Link to={cta.href}>
                <Button size="xl" className="bg-white text-primary hover:bg-white/90">
                  {cta.button}
                </Button>
              </Link>
            )}
          </div>
        </section>
      )}
    </SiteLayout>
  );
};

export default AboutPage;
